import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  User,
  Moon,
  Sun,
  LogOut,
} from "lucide-react";
import { useCartStore } from "../store/cartStore";
import { useAuthStore } from "../store/authStore";
import { Badge } from "./ui/badge";
import { useTheme } from "../hooks/useTheme";

export default function Navbar() {
  const cartItems = useCartStore((state) => state.items);
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const { theme, toggleTheme } = useTheme();
  const darkMode = theme === "dark";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const { user, profile, signOut } = useAuthStore();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    setIsMenuOpen(false);
  };

  // Get display name
  const displayName = profile?.username || user?.email?.split("@")[0] || "User";

  return (
    <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-md shadow-[0_4px_20px_-2px_rgba(180,83,9,0.06)] border-b border-border">
      <div className="container flex items-center justify-between h-16">
        {/* Logo & Brand */}
        <Link
          to="/"
          className="flex items-center space-x-1 text-foreground"
        >
          <span className="font-display font-semibold text-xl tracking-tight">
            <span className="text-primary">Apicoltura</span>
            <span className="text-foreground"> DePiccoli</span>
          </span>
        </Link>

        {/* Mobile Cart & Menu Buttons */}
        <div className="flex items-center gap-1 md:hidden">
          <Link
            to="/cart"
            aria-label="Carrello"
            className="relative p-2 rounded-full hover:bg-muted"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <ShoppingCart className="h-5 w-5 text-foreground/80" />
            {itemCount > 0 && (
              <Badge className="absolute top-0 right-0 h-4 min-w-4 justify-center p-0 text-[10px] leading-none">
                {itemCount}
              </Badge>
            )}
          </Link>
          <button
            className="text-foreground/80 p-2"
            aria-label="Apri menu"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Navigation Links - Desktop */}
        <div className="hidden md:flex items-center">
          <div className="flex space-x-4 mr-4 text-sm">
            <Link
              to="/products"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Prodotti
            </Link>
            <Link
              to="/il-miele"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Il miele
            </Link>
            <Link
              to="/ricette"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Ricette
            </Link>
            <Link
              to="/novita"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Novità
            </Link>
            {/*<Link
              to="/about"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Chi siamo
            </Link>*/}
            <Link
              to="/contact"
              className="text-foreground/80 hover:text-primary transition-colors link-hover px-2 py-1"
            >
              Contatti
            </Link>
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-2 border-l border-border pl-4">
            <button
              onClick={toggleTheme}
              aria-label={darkMode ? "Passa al tema chiaro" : "Passa al tema scuro"}
              className="focus:outline-none p-1.5 rounded-full hover:bg-muted"
            >
              {darkMode ? (
                <Sun className="h-4 w-4 text-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            <Link
              to="/cart"
              className="relative p-1.5 rounded-full hover:bg-muted"
            >
              <ShoppingCart className="h-4 w-4 text-foreground/80 hover:text-primary transition-colors" />
              {itemCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-3.5 min-w-3.5 justify-center p-0 text-[10px] leading-none">
                  {itemCount}
                </Badge>
              )}
            </Link>
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center space-x-1 text-foreground/80 hover:text-primary transition-colors p-1.5 rounded-full hover:bg-muted"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden md:inline text-xs ml-1">
                    {displayName}
                  </span>
                </button>
                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-card text-card-foreground rounded-md shadow-lg py-1 z-50 border border-border">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-primary"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profilo
                    </Link>
                    <Link
                      to="/orders"
                      className="block px-4 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-primary"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Ordini
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-xs text-foreground/80 hover:bg-muted hover:text-primary"
                    >
                      <div className="flex items-center">
                        <LogOut className="h-3 w-3 mr-2" />
                        Esci
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className="text-foreground/80 hover:text-primary transition-colors flex items-center p-1.5 rounded-full hover:bg-muted"
              >
                <User className="h-4 w-4" />
                <span className="hidden md:inline text-xs ml-1">Accedi</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background py-2 px-4 border-t border-border">
          <div className="flex flex-col space-y-2">
            <Link
              to="/products"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Prodotti
            </Link>
            <Link
              to="/il-miele"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Il miele
            </Link>
            <Link
              to="/ricette"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Ricette
            </Link>
            <Link
              to="/novita"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Novità
            </Link>
            {/*<Link
              to="/about"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Chi siamo
            </Link>*/}
            <Link
              to="/contact"
              className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contatti
            </Link>

            {user && (
              <>
                <Link
                  to="/profile"
                  className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profilo
                </Link>
                <Link
                  to="/orders"
                  className="text-foreground/80 hover:text-primary transition-colors py-2.5 text-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  I miei ordini
                </Link>
              </>
            )}

            {/* Mobile User Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleTheme}
                  aria-label={darkMode ? "Passa al tema chiaro" : "Passa al tema scuro"}
                  className="focus:outline-none p-2.5 rounded-full hover:bg-muted"
                >
                  {darkMode ? (
                    <Sun className="h-4 w-4 text-foreground" />
                  ) : (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>

              {user ? (
                <div className="flex items-center">
                  <span className="truncate max-w-[9rem] text-sm text-foreground/80 mr-2">
                    {displayName}
                  </span>
                  <button
                    onClick={handleSignOut}
                    className="text-red-500 text-sm flex items-center"
                  >
                    <LogOut className="h-3 w-3 mr-1" />
                    Esci
                  </button>
                </div>
              ) : (
                <Link
                  to="/auth"
                  className="text-primary text-sm flex items-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="h-4 w-4 mr-1" />
                  Accedi
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
